import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  where, doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import Paginator from '../components/Paginator';

const GROUP_PAGE_SIZE = 20;

const formatTs = (ts) => {
  if (!ts?.seconds) return '—';
  return new Date(ts.seconds * 1000).toLocaleString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDateVal = (val) => {
  if (!val) return '—';
  if (val?.seconds) {
    return new Date(val.seconds * 1000).toLocaleDateString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }
  return String(val);
};

const tsToMs = (ts) => (ts?.seconds ? ts.seconds * 1000 : 0);

const Check = ({ ok }) => (
  <span style={{ fontSize: 15, color: ok ? '#22C55E' : '#EF4444', lineHeight: 1 }}>
    {ok ? '✓' : '✗'}
  </span>
);

const AckChecks = ({ items, fields }) => {
  if (fields) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        {fields.map((f, i) => <Check key={i} ok={!!f} />)}
      </div>
    );
  }
  if (Array.isArray(items) && items.length) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        {items.slice(0, 4).map((v, i) => <Check key={i} ok={!!v} />)}
      </div>
    );
  }
  return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>;
};

const getAckFields = (rec) => {
  if (Array.isArray(rec.acknowledgedItems) && rec.acknowledgedItems.length) return null;
  const keys = ['ackInsurance', 'ackSafety', 'ackLicense', 'ackIdentity'];
  if (keys.some(k => k in rec)) return keys.map(k => rec[k]);
  const boolKeys = Object.keys(rec).filter(k => k.startsWith('ack') || k.startsWith('acknowledged'));
  if (boolKeys.length) return boolKeys.slice(0, 4).map(k => rec[k]);
  return null;
};

function DriverRow({ rec, isLast }) {
  const ackFields = getAckFields(rec);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px 24px',
      padding: '13px 20px',
      borderBottom: isLast ? 'none' : '1px solid var(--surface2)',
      background: 'rgba(255,255,255,0.012)',
    }}>
      {/* Driver identity */}
      <div style={{ flex: '1 1 160px', minWidth: 140 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>
          {rec.driverName || '—'}
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
          Confirmed {formatTs(rec.confirmedAt)}
        </div>
      </div>

      {/* Meta chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>
            Shift Start
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
            {formatDateVal(rec.shiftStartDate || rec.bookingStartDate)}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>
            Re-Hire
          </div>
          <div style={{ fontSize: 13 }}>
            {rec.isReHire == null
              ? <span style={{ color: 'var(--text-muted)' }}>—</span>
              : rec.isReHire
                ? <span style={{ color: '#22C55E', fontWeight: 600 }}>Yes</span>
                : <span style={{ color: 'var(--text-secondary)' }}>No</span>
            }
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>
            Acknowledged
          </div>
          <AckChecks items={rec.acknowledgedItems} fields={ackFields} />
        </div>
      </div>
    </div>
  );
}

function GroupCard({ group, isExpanded, onToggle }) {
  const { hirer, job, records } = group;
  const isMultiple = records.length > 1;
  const showContent = !isMultiple || isExpanded;
  const [hovered, setHovered] = useState(false);

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--surface2)',
      borderRadius: 14,
      overflow: 'hidden',
    }}>
      {/* Group header */}
      <div
        onClick={isMultiple ? onToggle : undefined}
        onMouseEnter={() => isMultiple && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: '15px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: isMultiple ? 'pointer' : 'default',
          borderBottom: showContent ? '1px solid var(--surface2)' : 'none',
          background: hovered ? 'rgba(255,255,255,0.025)' : 'transparent',
          transition: 'background 0.15s',
          userSelect: 'none',
        }}
      >
        {/* Hirer + job info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: 'var(--text)',
            marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {hirer}
          </div>
          <div style={{
            fontSize: 13, color: 'var(--text-secondary)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {job}
          </div>
        </div>

        {/* Right side: driver count badge + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{
            background: isMultiple ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${isMultiple ? 'rgba(59,130,246,0.3)' : 'var(--surface2)'}`,
            color: isMultiple ? '#3B82F6' : 'var(--text-muted)',
            borderRadius: 20,
            padding: '3px 11px',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {records.length} {records.length === 1 ? 'driver' : 'drivers'}
          </span>

          {isMultiple && (
            <span style={{
              color: 'var(--text-muted)',
              fontSize: 13,
              display: 'inline-block',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}>
              ▼
            </span>
          )}
        </div>
      </div>

      {/* Driver rows */}
      {showContent && (
        <div>
          {records.map((rec, i) => (
            <DriverRow
              key={rec.id}
              rec={rec}
              isLast={i === records.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function InsuranceConfirmations() {
  const [records, setRecords]           = useState([]);
  const [alerts, setAlerts]             = useState([]);
  const [loadingRec, setLoadingRec]     = useState(true);
  const [filterHirer, setFilterHirer]   = useState('');
  const [filterDriver, setFilterDriver] = useState('');
  const [filterFrom, setFilterFrom]     = useState('');
  const [filterTo, setFilterTo]         = useState('');
  const [page, setPage]                 = useState(1);
  const [dismissing, setDismissing]     = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    const q = query(
      collection(db, 'preHireConfirmations'),
      orderBy('confirmedAt', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingRec(false);
    }, (err) => {
      console.error('preHireConfirmations error:', err);
      setLoadingRec(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'adminNotifications'),
      where('type', '==', 'insurance_confirm_missing'),
      where('read', '==', false),
    );
    const unsub = onSnapshot(q, (snap) => {
      setAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('adminNotifications error:', err);
    });
    return unsub;
  }, []);

  const handleDismissAlert = async (alertId) => {
    setDismissing(alertId);
    try {
      await updateDoc(doc(db, 'adminNotifications', alertId), {
        read: true,
        readAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    } finally {
      setDismissing(null);
    }
  };

  const handleDismissAll = async () => {
    await Promise.all(alerts.map(a =>
      updateDoc(doc(db, 'adminNotifications', a.id), {
        read: true,
        readAt: serverTimestamp(),
      }).catch(console.error)
    ));
  };

  const filtered = useMemo(() => {
    return records.filter(r => {
      const hirer  = (r.hirerName || r.hirerCompanyName || '').toLowerCase();
      const driver = (r.driverName || '').toLowerCase();
      if (filterHirer  && !hirer.includes(filterHirer.toLowerCase()))   return false;
      if (filterDriver && !driver.includes(filterDriver.toLowerCase()))  return false;
      if (filterFrom || filterTo) {
        const ms = tsToMs(r.confirmedAt);
        if (!ms) return false;
        if (filterFrom && ms < new Date(filterFrom).getTime()) return false;
        if (filterTo   && ms > new Date(filterTo).getTime() + 86_400_000 - 1) return false;
      }
      return true;
    });
  }, [records, filterHirer, filterDriver, filterFrom, filterTo]);

  const groupedData = useMemo(() => {
    const map = new Map();
    filtered.forEach(rec => {
      const hirer = rec.hirerName || rec.hirerCompanyName || '—';
      const job   = rec.jobTitle  || rec.title            || '—';
      const key   = `${hirer}|||${job}`;
      if (!map.has(key)) map.set(key, { hirer, job, key, records: [] });
      map.get(key).records.push(rec);
    });
    return Array.from(map.values());
  }, [filtered]);

  useEffect(() => { setPage(1); }, [filterHirer, filterDriver, filterFrom, filterTo]);

  const hasFilters  = filterHirer || filterDriver || filterFrom || filterTo;
  const totalPages  = Math.ceil(groupedData.length / GROUP_PAGE_SIZE);
  const visibleGroups = groupedData.slice((page - 1) * GROUP_PAGE_SIZE, page * GROUP_PAGE_SIZE);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setFilterHirer('');
    setFilterDriver('');
    setFilterFrom('');
    setFilterTo('');
  };

  return (
    <div className="animate-fade">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>
          Insurance Confirmations
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
          Pre-hire insurance confirmations submitted before job shifts
        </p>
      </div>

      {/* Missing-confirmation alerts */}
      {alerts.length > 0 && (
        <div style={{
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 12, padding: '16px 20px', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🚨</span>
              <span style={{ color: '#EF4444', fontWeight: 700, fontSize: 15 }}>
                {alerts.length} Missing Confirmation{alerts.length > 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={handleDismissAll}
              style={{
                background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#EF4444', borderRadius: 8, padding: '5px 12px', fontSize: 12,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Dismiss all
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {alerts.map(alert => (
              <div key={alert.id} style={{
                background: 'var(--surface)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 10, padding: '12px 14px',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13, marginBottom: 3 }}>
                    {alert.jobTitle || 'Job'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    Hirer: <span style={{ color: 'var(--text-secondary)' }}>{alert.hirerName || '—'}</span>
                    {' · '}
                    Driver: <span style={{ color: 'var(--text-secondary)' }}>{alert.driverName || '—'}</span>
                  </div>
                </div>
                {alert.jobId && (
                  <span style={{
                    background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
                    color: '#3B82F6', borderRadius: 6, padding: '3px 8px',
                    fontSize: 11, fontFamily: 'monospace',
                  }}>
                    Job: {alert.jobId.slice(0, 10)}…
                  </span>
                )}
                <button
                  onClick={() => handleDismissAlert(alert.id)}
                  disabled={dismissing === alert.id}
                  style={{
                    background: 'transparent', border: '1px solid var(--border)',
                    color: 'var(--text-muted)', borderRadius: 8, padding: '5px 12px',
                    fontSize: 12, cursor: dismissing === alert.id ? 'not-allowed' : 'pointer',
                  }}
                >
                  {dismissing === alert.id ? '…' : 'Dismiss'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Filter by hirer…"
          value={filterHirer}
          onChange={e => setFilterHirer(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--surface2)',
            borderRadius: 8, color: 'var(--text)', padding: '8px 12px',
            fontSize: 13, outline: 'none', flex: '1 1 140px', minWidth: 140,
          }}
        />
        <input
          type="text"
          placeholder="Filter by driver…"
          value={filterDriver}
          onChange={e => setFilterDriver(e.target.value)}
          style={{
            background: 'var(--surface)', border: '1px solid var(--surface2)',
            borderRadius: 8, color: 'var(--text)', padding: '8px 12px',
            fontSize: 13, outline: 'none', flex: '1 1 140px', minWidth: 140,
          }}
        />
        <input
          type="date"
          value={filterFrom}
          onChange={e => setFilterFrom(e.target.value)}
          title="Confirmed on or after"
          style={{
            background: 'var(--surface)', border: '1px solid var(--surface2)',
            borderRadius: 8, color: filterFrom ? 'var(--text)' : 'var(--text-muted)',
            padding: '8px 12px', fontSize: 13, outline: 'none', colorScheme: 'dark',
          }}
        />
        <input
          type="date"
          value={filterTo}
          onChange={e => setFilterTo(e.target.value)}
          title="Confirmed on or before"
          style={{
            background: 'var(--surface)', border: '1px solid var(--surface2)',
            borderRadius: 8, color: filterTo ? 'var(--text)' : 'var(--text-muted)',
            padding: '8px 12px', fontSize: 13, outline: 'none', colorScheme: 'dark',
          }}
        />
        {hasFilters && (
          <button
            onClick={clearFilters}
            style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
              color: '#EF4444', borderRadius: 8, padding: '8px 14px',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            Clear
          </button>
        )}
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 'auto' }}>
          {groupedData.length} job{groupedData.length !== 1 ? 's' : ''}
          {' · '}
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Content */}
      {loadingRec ? (
        <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>
          Loading confirmations…
        </div>
      ) : records.length === 0 ? (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--surface2)',
          borderRadius: 14, padding: 48, textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
          <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: 4 }}>
            No confirmations yet
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Pre-hire insurance confirmations will appear here
          </div>
        </div>
      ) : groupedData.length === 0 ? (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--surface2)',
          borderRadius: 14, padding: 40, textAlign: 'center', color: 'var(--text-muted)',
        }}>
          No records match the current filters.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibleGroups.map(group => (
              <GroupCard
                key={group.key}
                group={group}
                isExpanded={expandedGroups.has(group.key)}
                onToggle={() => toggleGroup(group.key)}
              />
            ))}
          </div>

          <Paginator
            page={page}
            totalPages={totalPages}
            totalItems={groupedData.length}
            pageSize={GROUP_PAGE_SIZE}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}
